import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { FaSearch, FaStar, FaCheck } from "react-icons/fa";
import { toast } from "react-toastify";
import { getProducts, applyManualProductRatings, getProductManualRatingTotals } from "../../services/ClientService";

const PAGE_SIZE = 12;
const STARS = [5, 4, 3, 2, 1];

const getProductImage = (product) => {
    if (product.images && product.images.length > 0 && product.images[0]?.preview) {
        return product.images[0].preview;
    }
    if (product.productOptions && product.productOptions.length > 0) {
        const firstOption = product.productOptions[0];
        if (firstOption.images && firstOption.images.length > 0 && firstOption.images[0]?.preview) {
            return firstOption.images[0].preview;
        }
    }
    try {
        return require("../../../src/no-image-icon-23494.png");
    } catch {
        return "";
    }
};

const ManualRating = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [products, setProducts] = useState([]);
    const [productsSkip, setProductsSkip] = useState(0);
    const [hasMoreProducts, setHasMoreProducts] = useState(true);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedStars, setSelectedStars] = useState(5);
    const [countInput, setCountInput] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [customTotals, setCustomTotals] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 500);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const fetchProductsPage = useCallback(async (skip = 0) => {
        if (skip === 0) {
            setIsLoadingProducts(true);
            setHasMoreProducts(true);
        } else {
            setIsFetchingMore(true);
        }
        try {
            const data = await getProducts({
                searchTerm: debouncedSearchTerm,
                skip,
                limit: PAGE_SIZE,
                isActive: [true, false],
            });
            if (data.success) {
                const fetched = data.data || [];
                if (skip === 0) {
                    setProducts(fetched);
                } else {
                    setProducts((prev) => [...prev, ...fetched]);
                }
                setHasMoreProducts(fetched.length === PAGE_SIZE);
                setProductsSkip(skip + fetched.length);
            } else {
                if (skip === 0) setProducts([]);
                setHasMoreProducts(false);
            }
        } catch {
            if (skip === 0) setProducts([]);
            setHasMoreProducts(false);
        } finally {
            setIsLoadingProducts(false);
            setIsFetchingMore(false);
        }
    }, [debouncedSearchTerm]);

    useEffect(() => {
        setProductsSkip(0);
        setHasMoreProducts(true);
        fetchProductsPage(0);
    }, [debouncedSearchTerm, fetchProductsPage]);

    const loadCustomTotals = async (product) => {
        if (!product?._id) return;
        try {
            const res = await getProductManualRatingTotals({ productId: product._id });
            if (res.success && res.data) {
                setCustomTotals(res.data);
            } else {
                setCustomTotals(null);
            }
        } catch {
            setCustomTotals(null);
        }
    };

    const handleSelectProduct = (p) => {
        setSelectedProduct(p);
        setCountInput("");
        setSelectedStars(5);
        loadCustomTotals(p);
    };

    const handleProductListScroll = (e) => {
        const el = e.currentTarget;
        if (
            el.scrollTop + el.clientHeight >= el.scrollHeight - 50 &&
            !isFetchingMore &&
            !isLoadingProducts &&
            hasMoreProducts
        ) {
            fetchProductsPage(productsSkip);
        }
    };

    const handleSubmit = async () => {
        if (!selectedProduct) {
            toast.error("Select a product first");
            return;
        }
        const n = parseInt(countInput, 10);
        if (!Number.isInteger(n) || n < 1) {
            toast.error("Enter a valid count (whole number ≥ 1)");
            return;
        }
        setSubmitting(true);
        try {
            const res = await applyManualProductRatings({
                productId: selectedProduct._id,
                rating: selectedStars,
                count: n,
            });
            if (res.success) {
                toast.success(res.message || "Manual ratings saved");
                setCountInput("");
                await loadCustomTotals(selectedProduct);
            } else {
                toast.error(res.message || res.error || "Could not save ratings");
            }
        } catch (err) {
            toast.error(err?.message || "Could not save ratings");
        } finally {
            setSubmitting(false);
        }
    };

    const breakdown = customTotals?.customRatingsBreakdown;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/40 p-4 md:p-8">
            <div className="max-w-5xl mx-auto">
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Manual rating</h1>
                    <p className="text-slate-600 mt-2 max-w-2xl">
                        Choose a product, pick a star level (1–5), enter how many ratings to add, then submit. These counts are stored as{" "}
                        <span className="font-semibold text-indigo-700">custom</span> entries and merge with real customer ratings everywhere the app shows totals.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-5">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Search products</label>
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                                placeholder="Name or product ID…"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div
                            className="mt-4 h-[420px] overflow-y-auto space-y-2 pr-1 border border-slate-100 rounded-xl"
                            onScroll={handleProductListScroll}
                        >
                            {isLoadingProducts && productsSkip === 0 ? (
                                <div className="flex items-center justify-center h-full min-h-[200px]">
                                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
                                </div>
                            ) : products.length === 0 ? (
                                <p className="text-sm text-slate-500 p-4">No products match this search.</p>
                            ) : (
                                <>
                                    {products.map((p) => (
                                        <button
                                            type="button"
                                            key={p._id}
                                            onClick={() => handleSelectProduct(p)}
                                            className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition ${
                                                selectedProduct?._id === p._id
                                                    ? "border-indigo-500 bg-indigo-50/80 ring-1 ring-indigo-200"
                                                    : "border-transparent hover:border-slate-200 hover:bg-slate-50"
                                            }`}
                                        >
                                            <img src={getProductImage(p)} alt="" className="w-12 h-12 rounded-lg object-cover bg-slate-100" />
                                            <div className="min-w-0 flex-1">
                                                <div className="font-medium text-slate-900 truncate">{p.productName}</div>
                                                <div className="text-xs text-slate-500 truncate">{p.productId || p._id}</div>
                                            </div>
                                            {selectedProduct?._id === p._id && <FaCheck className="text-indigo-600 shrink-0" />}
                                        </button>
                                    ))}
                                    {isFetchingMore && (
                                        <div className="flex items-center justify-center py-3">
                                            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-indigo-500" />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-5 flex flex-col">
                        {!selectedProduct && (
                            <p className="text-slate-500 text-sm flex-1 flex items-center justify-center text-center px-4">
                                Select a product on the left to configure manual ratings.
                            </p>
                        )}
                        {selectedProduct && (
                            <>
                                <div className="flex gap-3 mb-6">
                                    <img src={getProductImage(selectedProduct)} alt="" className="w-16 h-16 rounded-xl object-cover bg-slate-100" />
                                    <div className="min-w-0">
                                        <h2 className="font-semibold text-lg text-slate-900 leading-snug">{selectedProduct.productName}</h2>
                                        <p className="text-xs text-slate-500 mt-1">Custom totals (reference only)</p>
                                        {breakdown && (
                                            <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                                {[5, 4, 3, 2, 1].map((s) => (
                                                    <span key={s} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                                                        {s}★: {Number(breakdown[String(s)]) || 0}
                                                    </span>
                                                ))}
                                                <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 font-medium">
                                                    custom total: {customTotals?.customTotalRatings ?? 0}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mb-5">
                                    <p className="text-sm font-semibold text-slate-700 mb-2">Star level to add</p>
                                    <div className="flex flex-wrap gap-2">
                                        {STARS.map((s) => (
                                            <button
                                                type="button"
                                                key={s}
                                                onClick={() => setSelectedStars(s)}
                                                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium transition ${
                                                    selectedStars === s
                                                        ? "border-amber-400 bg-amber-50 text-amber-900"
                                                        : "border-slate-200 text-slate-700 hover:bg-slate-50"
                                                }`}
                                            >
                                                <FaStar className={selectedStars === s ? "text-amber-500" : "text-slate-300"} />
                                                {s} stars
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">How many ratings to add</label>
                                    <input
                                        type="number"
                                        min={1}
                                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                                        placeholder="e.g. 100"
                                        value={countInput}
                                        onChange={(e) => setCountInput(e.target.value)}
                                    />
                                    <p className="text-xs text-slate-500 mt-2">
                                        Customer app totals combine real order ratings with these custom counts (e.g. ten real 5★ plus one hundred manual 5★ shows as 5★ × 110 when all are five-star).
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    disabled={submitting}
                                    onClick={handleSubmit}
                                    className="mt-auto w-full py-3 rounded-xl font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                >
                                    {submitting ? "Saving…" : "Submit manual ratings"}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManualRating;
